import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ShoppingCart, FileText, Heart, CreditCard, Sparkles, Zap, Shield, TrendingUp } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const OperationsAgentLibrary = () => {
  const navigate = useNavigate();
  const [agentsRef, agentsVisible] = useScrollAnimation({ threshold: 0.1 });
  const [reuseRef, reuseVisible] = useScrollAnimation({ threshold: 0.1 });

  const agents = [
    {
      id: 1,
      title: 'Order Entry Management Agent',
      description: 'Parses emailed POs (OCR + LLM), validates details, and creates/updates ERP orders while flagging duplicates and exceptions.',
      icon: <ShoppingCart className="w-8 h-8" />,
      color: 'from-blue-500/20 to-indigo-500/20',
      iconColor: 'text-blue-400',
      features: ['OCR parsing', 'ERP integration', 'Duplicate detection', 'Exception flagging']
    },
    {
      id: 2,
      title: 'Technical Language Interpreter AI Agent',
      description: 'Converts jargon-heavy docs into plain-English, structure-preserving Markdown - glossaries included - without losing precision.',
      icon: <FileText className="w-8 h-8" />,
      color: 'from-green-500/20 to-emerald-500/20',
      iconColor: 'text-green-400',
      features: ['Jargon translation', 'Structure preservation', 'Glossary generation', 'Precision maintained']
    },
    {
      id: 3,
      title: 'Care Follow-Up & Notification Agent',
      description: 'Detects care gaps (e.g., overdue/elevated HbA1c), creates tasks with due dates, and drafts patient notifications with audit trails.',
      icon: <Heart className="w-8 h-8" />,
      color: 'from-pink-500/20 to-rose-500/20',
      iconColor: 'text-pink-400',
      features: ['Gap detection', 'Task automation', 'Patient outreach', 'Audit trails']
    },
    {
      id: 4,
      title: 'Credit Card Origination Agent',
      description: 'Orchestrates KYC/AML, IDV, credit/fraud checks, and income verification to auto-decide approve/refer/decline with explainable rationale and e-sign handoff.',
      icon: <CreditCard className="w-8 h-8" />,
      color: 'from-orange-500/20 to-amber-500/20',
      iconColor: 'text-orange-400',
      features: ['KYC/AML checks', 'Fraud detection', 'Auto-decisioning', 'E-sign integration']
    }
  ];

  const handleBookDemo = () => {
    window.open('https://calendly.com/speakmulti/introduction', '_blank');
  };

  const handleHowItWorks = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('how-oneplace-works');
      if (element) {
        const headerOffset = 100;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Animated background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 via-transparent to-orange-500/10 animate-gradient-shift" />
        
        {/* Grid pattern overlay */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(18, 215, 232, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(18, 215, 232, 0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}
        />

        <div className="light-full-container relative z-10">
          <div className="light-content-container">
            <div className="max-w-4xl mx-auto text-center">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#12D7E8]/10 border border-[#12D7E8]/20 mb-6">
                <Sparkles className="w-4 h-4 text-[#12D7E8]" />
                <span className="text-sm font-semibold text-[#12D7E8]">Operations Agent Library</span>
              </div>

              {/* Title */}
              <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-white via-white to-[#12D7E8] bg-clip-text text-transparent">
                Streamline Operations with <span className="text-[#12D7E8]">Intelligent Automation Agents</span>
              </h1>

              {/* Subtitle */}
              <p className="text-xl text-[rgba(255,255,255,0.7)] mb-10 leading-relaxed max-w-3xl mx-auto">
                Automate order management, document translation, care coordination, and credit decisioning - all within OnePlace's unified agentic platform. Drive operational excellence at enterprise scale.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-10">
                <button 
                  onClick={handleBookDemo}
                  className="group px-8 py-4 bg-[#12D7E8] text-black font-semibold rounded-lg hover:bg-[#33E1FF] transition-all duration-300 flex items-center gap-2 shadow-lg shadow-[#12D7E8]/20 hover:shadow-[#12D7E8]/40 hover:scale-105"
                >
                  Book a Demo
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                
                <button 
                  onClick={handleHowItWorks}
                  className="group px-8 py-4 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white font-semibold rounded-lg hover:bg-[rgba(255,255,255,0.1)] hover:border-[#12D7E8]/50 transition-all duration-300 flex items-center gap-2"
                >
                  How It Works
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-8 mt-16 max-w-2xl mx-auto">
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#12D7E8] mb-2">10+</div>
                  <div className="text-sm text-[rgba(255,255,255,0.6)]">Operations Agents</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#12D7E8] mb-2">70%</div>
                  <div className="text-sm text-[rgba(255,255,255,0.6)]">Cost Reduction</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#12D7E8] mb-2">99%</div>
                  <div className="text-sm text-[rgba(255,255,255,0.6)]">Accuracy</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Agents Grid Section */}
      <section ref={agentsRef} className="section-spacing relative">
        <div className="light-full-container">
          <div className="light-content-container">
            <div className="max-w-7xl mx-auto">
              {/* Section Header */}
              <div className="text-center mb-16">
                <h2 className="display-large mb-4">Meet Your Operations <span className="text-[#12D7E8]">Agent Team</span></h2>
                <p className="text-lg text-[rgba(255,255,255,0.7)] max-w-2xl mx-auto">
                  Four specialized agents powering critical workflows across order management, document processing, healthcare, and financial services
                </p>
              </div>

              {/* Agents Grid */}
              <div className="grid md:grid-cols-2 gap-6">
                {agents.map((agent, index) => (
                  <div
                    key={agent.id}
                    onClick={() => {
                      if (agent.id === 1) navigate('/agent-library/operations/order-entry');
                      if (agent.id === 2) navigate('/agent-library/operations/tech-language-interpreter');
                      if (agent.id === 3) navigate('/agent-library/operations/care-follow-up');
                      if (agent.id === 4) navigate('/agent-library/operations/credit-card-origination');
                    }}
                    className={`agent-library-card group ${agentsVisible ? 'animate-fade-in-up' : 'opacity-0'} cursor-pointer`}
                    style={{ animationDelay: `${index * 150}ms` }}
                  >
                    {/* Gradient background */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${agent.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl`} />
                    
                    <div className="relative z-10">
                      {/* Icon */}
                      <div className={`inline-flex p-4 rounded-xl bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] mb-6 ${agent.iconColor} group-hover:scale-110 transition-transform duration-300`}>
                        {agent.icon}
                      </div>

                      {/* Title */}
                      <h3 className="text-2xl font-bold mb-4 text-white group-hover:text-[#12D7E8] transition-colors">
                        {agent.title}
                      </h3>

                      {/* Description */}
                      <p className="text-[rgba(255,255,255,0.7)] leading-relaxed mb-6">
                        {agent.description}
                      </p>

                      {/* Features */}
                      <div className="grid grid-cols-2 gap-3">
                        {agent.features.map((feature, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm">
                            <Zap className="w-4 h-4 text-[#12D7E8] flex-shrink-0" />
                            <span className="text-[rgba(255,255,255,0.6)]">{feature}</span>
                          </div>
                        ))}
                      </div>

                      {/* Learn More Badge - All agents now have detail pages */}
                      <div className="flex items-center gap-2 mt-6 text-[#12D7E8] opacity-0 group-hover:opacity-100 transition-opacity">
                        <span className="text-sm font-semibold">Learn more</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Build Once, Reuse Everywhere Section */}
      <section ref={reuseRef} className="section-spacing relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#12D7E8]/5 to-transparent" />
        
        <div className="light-full-container relative z-10">
          <div className="light-content-container">
            <div className="max-w-6xl mx-auto">
              <div className={`relative bg-gradient-to-br from-[rgba(255,255,255,0.03)] to-[rgba(255,255,255,0.01)] border border-[rgba(255,255,255,0.1)] rounded-3xl p-12 md:p-16 ${reuseVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#12D7E8]/10 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl" />
                
                <div className="relative z-10">
                  <div className="max-w-4xl mx-auto text-center">
                    {/* Icon */}
                    <div className="inline-flex p-4 rounded-2xl bg-[#12D7E8]/10 border border-[#12D7E8]/20 mb-6">
                      <Shield className="w-8 h-8 text-[#12D7E8]" />
                    </div>

                    {/* Title */}
                    <h2 className="text-4xl md:text-5xl font-bold mb-6">
                      Automate Once. <span className="text-[#12D7E8]">Scale Infinitely.</span>
                    </h2>

                    {/* Description */}
                    <p className="text-lg text-[rgba(255,255,255,0.7)] leading-relaxed mb-8">
                      OnePlace's unified agentic platform lets you design operational workflows once, then deploy them across departments, systems, and business units. No siloed tools, no manual handoffs - just seamless, intelligent automation that scales with your enterprise.
                    </p>

                    {/* Features Grid */}
                    <div className="grid md:grid-cols-3 gap-6 mb-10">
                      <div className="flex flex-col items-center gap-3 p-6 rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.05)]">
                        <Zap className="w-6 h-6 text-[#12D7E8]" />
                        <h3 className="font-semibold text-white">Zero Touch Processing</h3>
                        <p className="text-sm text-[rgba(255,255,255,0.6)]">Fully automated workflows from trigger to completion</p>
                      </div>
                      <div className="flex flex-col items-center gap-3 p-6 rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.05)]">
                        <Shield className="w-6 h-6 text-[#12D7E8]" />
                        <h3 className="font-semibold text-white">Enterprise Security</h3>
                        <p className="text-sm text-[rgba(255,255,255,0.6)]">Bank-grade compliance and audit capabilities</p>
                      </div>
                      <div className="flex flex-col items-center gap-3 p-6 rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.05)]">
                        <TrendingUp className="w-6 h-6 text-[#12D7E8]" />
                        <h3 className="font-semibold text-white">Measurable ROI</h3>
                        <p className="text-sm text-[rgba(255,255,255,0.6)]">Track efficiency gains and cost savings in real-time</p>
                      </div>
                    </div>

                    {/* CTA */}
                    <button 
                      onClick={handleBookDemo}
                      className="group px-8 py-4 bg-[#12D7E8] text-black font-semibold rounded-lg hover:bg-[#33E1FF] transition-all duration-300 inline-flex items-center gap-2 shadow-lg shadow-[#12D7E8]/20 hover:shadow-[#12D7E8]/40 hover:scale-105"
                    >
                      See It In Action
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <style jsx>{`
        @keyframes gradient-shift {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.8;
          }
        }

        .animate-gradient-shift {
          animation: gradient-shift 8s ease-in-out infinite;
        }

        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
        }

        .agent-library-card {
          position: relative;
          padding: 2rem;
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 1rem;
          transition: all 0.3s ease;
          overflow: hidden;
        }

        .agent-library-card:hover {
          transform: translateY(-8px);
          border-color: rgba(18, 215, 232, 0.3);
          box-shadow: 0 20px 60px rgba(18, 215, 232, 0.15);
        }
      `}</style>
    </div>
  );
};

export default OperationsAgentLibrary;
